import { useContext, useMemo } from 'react';
import type { ComboboxInputPropsInterface, ComboboxOnChangeType } from '@zillow/constellation-10';
import { Box, Combobox, FieldSet, FormFieldContext, FormHelp, Icon, IconButton } from '@zillow/constellation-10';
import { IconClockFilled, IconCloseFilled, IconMinusFilled, IconPlusFilled } from '@zillow/constellation-icons';
import { TourDuration } from '@zg-rentals/ts-z-calendar-client';

import { convert12HourTimeTo24HourTime, createAvailabilityTimeOptions } from '../../../utils/availabilityTimeUtils';
import type { TimeSlot } from '../../../types/TourAvailabilityCards';
import { formatTimeString } from '../../../utils/formatTime';

interface TourAvailabilityCardTimeSlotProps {
  timeSlot: TimeSlot;
  onAddTimeSlot: () => void;
  onChangeTimeSlot: (timeSlot: TimeSlot, field: 'start' | 'end') => ComboboxOnChangeType<string>;
  onRemoveTimeSlot: () => void;
  isTimeSlotRemovalDisabled?: boolean;
  tourDuration?: TourDuration;
}

const TourAvailabilityCardTimeSlot = ({
  timeSlot,
  onAddTimeSlot,
  onChangeTimeSlot,
  onRemoveTimeSlot,
  isTimeSlotRemovalDisabled,
  tourDuration = TourDuration.MINUTES_30,
}: TourAvailabilityCardTimeSlotProps) => {
  const startAvailabilityTimeOptions = useMemo(() => createAvailabilityTimeOptions(tourDuration), [tourDuration]);
  const endAvailabilityTimeOptions = useMemo(() => {
    const selectedStartTime = timeSlot.start ? formatTimeString(timeSlot.start, { showMeridiem: true }) : '';
    return createAvailabilityTimeOptions(tourDuration, convert12HourTimeTo24HourTime(selectedStartTime));
  }, [tourDuration, timeSlot.start]);
  const hasError = Boolean(timeSlot.errorMessage);
  // FieldSet replaces the surrounding FormFieldContext rather than merging into it,
  // so the enclosing field's state has to be forwarded or the inputs silently lose it.
  const { disabled, required } = useContext(FormFieldContext);

  return (
    <FieldSet
      aria-label="Time frame"
      disabled={disabled}
      required={required}
      error={hasError}
      description={
        hasError ? (
          <FormHelp data-testid="TourAvailabilityCardTimeSlot-ErrorMessage">{timeSlot.errorMessage}</FormHelp>
        ) : undefined
      }
    >
      {/* oxlint-disable-next-line constellation/no-raw-spacing-values */}
      <Box css={{ display: 'flex', gap: '8px', alignItems: 'center' }} data-testid="TourAvailabilityCardTimeSlot">
        <Combobox
          options={startAvailabilityTimeOptions}
          value={timeSlot.start ? formatTimeString(timeSlot.start, { showMeridiem: true }) : ''}
          onChange={onChangeTimeSlot(timeSlot, 'start')}
          renderClear={() => null}
          renderAdornment={(adornmentProps) => {
            const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;
            return (
              <Combobox.Adornment {...adornmentPropsWithoutRef}>
                <Icon render={<IconClockFilled />} />
              </Combobox.Adornment>
            );
          }}
          renderInput={(renderProps: ComboboxInputPropsInterface) => {
            const { ref: _ref, ...inputPropsWithoutRef } = renderProps;
            return (
              <Combobox.Input
                {...inputPropsWithoutRef}
                aria-label="Start time"
                data-testid="TourAvailabilityCardTimeSlot-StartTime-Input"
              />
            );
          }}
          css={{ flexGrow: '1' }}
          size="md"
          editable={false}
        />
        <Icon size="sm">
          <IconMinusFilled />
        </Icon>
        <Combobox
          options={endAvailabilityTimeOptions}
          value={timeSlot.end ? formatTimeString(timeSlot.end, { showMeridiem: true }) : ''}
          onChange={onChangeTimeSlot(timeSlot, 'end')}
          renderClear={() => null}
          renderAdornment={(adornmentProps) => {
            const { ref: _ref, ...adornmentPropsWithoutRef } = adornmentProps;
            return (
              <Combobox.Adornment {...adornmentPropsWithoutRef}>
                <Icon render={<IconClockFilled />} />
              </Combobox.Adornment>
            );
          }}
          renderInput={(renderProps: ComboboxInputPropsInterface) => {
            const { ref: _ref, ...inputPropsWithoutRef } = renderProps;
            return (
              <Combobox.Input
                {...inputPropsWithoutRef}
                aria-label="End time"
                data-testid="TourAvailabilityCardTimeSlot-EndTime-Input"
              />
            );
          }}
          css={{ flexGrow: '1' }}
          size="md"
          editable={false}
        />
        <IconButton
          emphasis="bare"
          shape="circle"
          size="sm"
          title="Remove"
          tone="neutral"
          onClick={onRemoveTimeSlot}
          disabled={isTimeSlotRemovalDisabled}
          data-testid="TourAvailabilityCardTimeSlot-RemoveTimeSlotButton"
        >
          <Icon>
            <IconCloseFilled />
          </Icon>
        </IconButton>
        <IconButton
          emphasis="bare"
          shape="circle"
          size="sm"
          title="Add time slot"
          tone="neutral"
          onClick={onAddTimeSlot}
          data-testid="TourAvailabilityCardTimeSlot-AddTimeSlotButton"
        >
          <Icon>
            <IconPlusFilled />
          </Icon>
        </IconButton>
      </Box>
    </FieldSet>
  );
};

export default TourAvailabilityCardTimeSlot;
